import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

import type { Body_login_login_access_token as AccessToken } from "@/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import useAuth, { isLoggedIn } from "@/hooks/useAuth";

function Login() {
  const { loginMutation, error, resetError } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/");
    }
  }, [navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<AccessToken>({
    mode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (data: AccessToken) => {
    if (isSubmitting) return;

    resetError();

    try {
      await loginMutation.mutateAsync(data);
    } catch {
      // Error is handled by useAuth hook
    }
  };

  return (
    <div className="min-h-screen bg-background relative flex items-center justify-center p-6 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-br from-foreground/20 via-transparent to-primary/15" />
      <Card className="w-full max-w-md relative z-20 bg-gradient-card shadow-card-legal border border-primary/25 backdrop-blur">
        <CardHeader className="text-center">
          <div className="flex flex-col items-center justify-center space-y-4 mb-4">
            <img
              src="/brickanta-logo-dark.svg"
              alt="Brickanta"
              className="h-10 w-auto drop-shadow-sm"
            />
            <p className="text-sm text-muted-foreground text-center max-w-xs">
              Fastighetsjuristens intelligenta AI-plattform
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="border border-accent/40 bg-accent/10 text-accent px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="username">Email</Label>
              <Input
                id="username"
                type="email"
                placeholder="din.email@exempel.se"
                {...register("username", {
                  required: "Email är obligatorisk",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Ogiltig email-adress",
                  },
                })}
                className={errors.username ? "border-red-300" : ""}
              />
              {errors.username && (
                <p className="text-red-600 text-xs">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Lösenord</Label>
              <Input
                id="password"
                type="password"
                placeholder="Ditt lösenord"
                {...register("password", {
                  required: "Lösenord är obligatoriskt",
                  minLength: {
                    value: 8,
                    message: "Lösenord måste vara minst 8 tecken",
                  },
                })}
                className={errors.password ? "border-red-300" : ""}
              />
              {errors.password && (
                <p className="text-red-600 text-xs">
                  {errors.password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-card-legal"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Loggar in..." : "Logga in"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default Login;
